import React, { useState, useMemo } from 'react';


import { Tabs, List, Segmented } from 'antd';

import styled from 'styled-components';

import { MODEL_AGENCY_TYPES, MODEL_AGENCY_TYPE_ORDERS } from '@constants/ModelAgencyTypes';

import { useAgencyModels } from '@hooks/API/Contact/UseAgencyModels';
import { useRemoveAgency } from '@hooks/API/Contact/UseContactAgencies';
import { getContactName, modelFullName } from '@hooks/API/Contact/UseContactsList';

import AgencyModelsDrawer from '@components/AgencyModels/AgencyModelsDrawer';
import CommissionsDrawer from '@components/ContactsCommissionsDrawers/CommissionsDrawer';
import ModelDrawer from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawer';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';

import AgencyModelsListItem from './AgencyModelsListItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledCard = styled(NwCard)`
    .ant-card-body {
        .ant-tabs {
            .ant-tabs-nav {
                margin: 0;
                padding: 0 2rem;

                .ant-tabs-nav-wrap {
                    .ant-tabs-nav-list {
                        .ant-tabs-tab {
                            font-size: ${(props) => props.theme.FontSizeSm};
                            text-transform: uppercase;
                            padding: 0.5rem 0;
                        }
                    }
                }
            }
        }
    }
`;

const AgencyModelsCard = ({ contactID, drawerMode }) => {
    //const { data: currentContactAgencies } = useContactAgencies(contactID);
    const { data: currentAgencyModels } = useAgencyModels(contactID);
    const [viewMode, setViewMode] = useState('LISTING'); // LISTING, INSERTING, EDITING
    const [currentModelAgency, setCurrentModelAgency] = useState(null);
    const [selectedType, setSelectedType] = useState('All');
    const [selectedModelStatus, setSelectedModelStatus] = useState('Booking');
    const [selectedCommissionsModelAgency, setSelectedCommissionsModelAgency] = useState(null);
    const { mutate: onRemoveAgency } = useRemoveAgency();
    const [modelPreviewID, setModelPreviewID] = useState(null);
    let motherExist = false;

    const excludedModels = [];
    if (currentAgencyModels) {
        currentAgencyModels.forEach((el) => {
            excludedModels.push(el.ModelID);
        });
    }

    const handleCloseDrawer = () => {
        setCurrentModelAgency(null);
        setViewMode('LISTING');
    };

    const handleEditModelAgency = (modelAgency) => {
        setCurrentModelAgency(modelAgency);
        setViewMode('EDITING');
    };

    const handleRemoveModelAgency = (modelAgency) => {
        onRemoveAgency({ ModelAgencyID: modelAgency.ID, AgencyID: contactID, ModelID: contactID });
    };

    const agencyModels = useMemo(() => {
        let agencyModelsList = [];
        if (currentAgencyModels) {
            agencyModelsList = currentAgencyModels.filter((modelagency) => {
                if (modelagency.TypeName === selectedType || selectedType === 'All') {
                    if (selectedModelStatus === 'Booking' && modelagency.Model.ContactSubType === 4) {
                        return true;
                    }
                    if (selectedModelStatus === 'Scouting' && modelagency.Model.ContactSubType === 5) {
                        return true;
                    }
                }
                return false;
            });
            agencyModelsList.sort((modelagency1, modelagency2) =>
                modelFullName(modelagency1.Model).toUpperCase() > modelFullName(modelagency2.Model).toUpperCase()
                    ? 1
                    : -1
            );
            agencyModelsList.sort((modelagency1, modelagency2) =>
                MODEL_AGENCY_TYPE_ORDERS[modelagency1.TypeName] >= MODEL_AGENCY_TYPE_ORDERS[modelagency2.TypeName]
                    ? 1
                    : -1
            );
            agencyModelsList.sort((modelagency1, modelagency2) => modelagency1.Dropped - modelagency2.Dropped);
        }
        return agencyModelsList;
    }, [currentAgencyModels, selectedType, selectedModelStatus]);

    const onChangeTab = (key) => {
        setSelectedType(key);
    };

    return (
        <>
            <StyledCard
                title="Models"
                icon={byPrefixAndName.fasl['user-friends']}
                fullheight
                extra={
                    <FlexContainer alignItems="center">
                        <NwAddButton
                            className="add-button"
                            ghost
                            onClick={() => {
                                setViewMode('INSERTING');
                            }}
                            label="Add new model"
                        />
                    </FlexContainer>
                }
                padding="no"
            >
                <Tabs
                    defaultActiveKey={selectedType}
                    onChange={onChangeTab}
                    items={MODEL_AGENCY_TYPES.map((modagtype, tabIndex) => {
                        return {
                            key: modagtype.value,
                            label: modagtype.value,
                        };
                    })}
                    tabBarExtraContent={
                        <Segmented
                            style={{ margin: '.5rem 0' }}
                            value={selectedModelStatus}
                            onChange={setSelectedModelStatus}
                            size="small"
                            options={['Booking', 'Scouting']}
                        />
                    }
                />
                {agencyModels.length === 0 ? (
                    <EmptyState message="no agencies" style={{ marginTop: '2.8rem' }} />
                ) : (
                    <List
                        itemLayout="horizontal"
                        dataSource={agencyModels}
                        renderItem={(item) => {
                            return (
                                <AgencyModelsListItem
                                    modelAgency={item}
                                    handleEdit={handleEditModelAgency}
                                    handleRemove={handleRemoveModelAgency}
                                    onModelPreview={setModelPreviewID}
                                    handleCommissions={setSelectedCommissionsModelAgency}
                                />
                            );
                        }}
                    />
                )}
            </StyledCard>
            {(viewMode === 'INSERTING' || viewMode === 'EDITING') && (
                <AgencyModelsDrawer
                    viewMode={viewMode}
                    excludedModels={excludedModels}
                    motherExist={motherExist}
                    agencyID={contactID}
                    agencyModel={currentModelAgency}
                    onClose={handleCloseDrawer}
                />
            )}
            {modelPreviewID && <ModelDrawer modelID={modelPreviewID} onClose={() => setModelPreviewID(null)} />}
            {selectedCommissionsModelAgency && (
                <CommissionsDrawer
                    agencyId={contactID}
                    agencyName={getContactName(contactID)}
                    modelId={selectedCommissionsModelAgency.ModelID || selectedCommissionsModelAgency.Model?.ID}
                    modelName={modelFullName(selectedCommissionsModelAgency.Model)}
                    mode={
                        selectedCommissionsModelAgency.TypeName?.toLowerCase() === 'placement'
                            ? 'asMotherAgency'
                            : 'toMotherAgency'
                    }
                    onClose={() => setSelectedCommissionsModelAgency(null)}
                />
            )}
        </>
    );
};

export default AgencyModelsCard;
